import React, {Suspense, useEffect} from 'react';
import {createPortal} from 'react-dom';
import {BrowserRouter as Router, useRoutes} from 'react-router-dom';
import {ApolloProvider} from '@apollo/client';
import {Toaster, toast} from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import {createApolloClient} from './lib/apollo';
import {createChannelApolloClient} from './lib/channelApollo';
import {createAuthApolloClient} from './lib/authApollo';
import {createFirmwareApolloClient} from './lib/firmwareApollo';
import {initializeFirebase} from './config/firebase';
import {AuthProvider} from './context/AuthContext';
import {CurrentUserProvider} from './context/CurrentUserContext';
import {ErrorBoundary} from './components/ErrorBoundary/ErrorBoundary';
import {LoadingSpinner} from './components/LoadingSpinner/LoadingSpinner';
import {routes} from './routes';
import {initSentry} from './config/sentry';
import {NotificationProvider} from './features/notifications/context/NotificationContext';
import {initializeServiceWorker} from './lib/serviceWorker';
import {HotkeyProvider} from './context/HotkeyContext';
import {GdprProvider} from './context/GdprContext';

// Initialize Sentry
initSentry();

function AppRoutes() {
  return useRoutes(routes);
}

function App() {
  const client = createApolloClient();
  createChannelApolloClient();
  createAuthApolloClient();
  createFirmwareApolloClient();

  useEffect(() => {
    // Initialize both service worker and Firebase
    initializeServiceWorker();
    initializeFirebase().catch(console.error);

    // Add online/offline event listeners
    const handleOnline = () => {
      toast.success('Connection restored', {
        id: 'online-status',
        duration: 3000
      });
    };

    const handleOffline = () => {
      toast.error('No internet connection', {
        id: 'offline-status',
        duration: 5000
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<ErrorBoundary fallback={true} />}>
      <ApolloProvider client={client}>
        <AuthProvider>
          <CurrentUserProvider>
            <GdprProvider>
            <NotificationProvider>
              <HotkeyProvider>
              <Router>
                <div className="min-h-screen bg-gray-100">
                  {/* YOJO-3393: portalled to <body>, outside #root, so toasts stay readable by
                      screen readers while a dialog hides the page (aria-hidden + inert on #root). */}
                  {createPortal(
                    <Toaster
                      position="bottom-right"
                      reverseOrder={false}
                    />,
                    document.body,
                  )}
                  <Suspense fallback={<LoadingSpinner />}>
                    <AppRoutes />
                  </Suspense>
                </div>
              </Router>
              </HotkeyProvider>
            </NotificationProvider>
            </GdprProvider>
          </CurrentUserProvider>
        </AuthProvider>
      </ApolloProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
